import type { ResultsFilterState } from "@/lib/results-filters";

const PDF_ENDPOINT = "/api/report/pdf";
export const REPORT_FILENAME = "climate-risk-report.pdf"; // no year/date, by design

async function safeFetch(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, init);
  } catch {
    // fetch itself threw — a real network failure, not an HTTP error status.
    // Wording deliberately identical to lib/auth.ts's doFetch; keep the two in
    // sync if either is ever reworded.
    throw new Error("Unable to reach the server. Please try again.");
  }
}

function postPdf(filters: ResultsFilterState): Promise<Response> {
  return safeFetch(PDF_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters }),
  });
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body?.message === "string") return body.message;
  } catch {
    // not JSON — fall through
  }
  return "PDF export failed.";
}

/**
 * request() in lib/auth.ts parses JSON and hard-redirects to /login on a
 * failed refresh — neither fits a binary download shown as an inline error,
 * so this does its own single 401 -> refresh -> retry.
 */
export async function fetchReportPdf(filters: ResultsFilterState): Promise<Blob> {
  let res = await postPdf(filters);

  if (res.status === 401) {
    const refresh = await safeFetch("/api/auth/refresh", { method: "POST" });
    if (!refresh.ok) throw new Error("Session expired. Please log in again.");
    res = await postPdf(filters);
  }

  if (!res.ok) throw new Error(await errorMessage(res));
  return res.blob();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function exportReportPdf(filters: ResultsFilterState): Promise<void> {
  downloadBlob(await fetchReportPdf(filters), REPORT_FILENAME);
}
